import type { AxiosResponse } from 'axios'
import { endpoints } from 'constants/endpoints'
import api from 'services/api'

import {
  toFairShareBMUByMaterialPayloadApi,
  toFairShareBMUPayloadApi,
  toFairShareBMUResponse,
  toFairShareByMaterialResponse,
  toFairShareOfficeByMaterialPayloadApi,
  toFairShareOfficeDownloadPayloadApi,
  toFairShareOfficePayloadApi,
  toFairShareOfficeResponse,
  toFairShareOpenWalletPayloadApi,
  toFairShareTrackingPayloadApi,
  toFairShareTrackingResponse,
  toMaterialLevelPayloadApi,
  toMaterialSharePayloadApi,
  toMaterialShareResponse,
} from './adapters'
import type {
  FairShareBMUByMaterialPayload,
  FairShareBMUPayload,
  FairShareOfficeByMaterialPayload,
  FairShareOfficeDownload,
  FairShareOfficePayload,
  FairShareOpenWalletPayload,
  FairShareTrackingPayload,
  MaterialLevelPayload,
  MaterialSharePayload,
} from './interfaces'

export const getFairShareBMU = async (fairSharePayload: FairShareBMUPayload) => {
  const { data } = await api.post(
    endpoints.fairShare.getBMUFairShare,
    toFairShareBMUPayloadApi(fairSharePayload),
  )

  return toFairShareBMUResponse(data.data)
}

export const getFairShareBMUByMaterial = async (
  fairShareByMaterialPayload: FairShareBMUByMaterialPayload,
) => {
  const { data } = await api.post(
    endpoints.fairShare.getFairShareByMaterial,
    toFairShareBMUByMaterialPayloadApi(fairShareByMaterialPayload),
  )

  return toFairShareByMaterialResponse(data.data)
}

export const downloadFairShareBMU = async (fairSharePayload: FairShareBMUPayload) => {
  const response: AxiosResponse<ArrayBuffer> = await api.post(
    endpoints.fairShare.downloadFairShareBMU,
    toFairShareBMUPayloadApi(fairSharePayload, true),
    { responseType: 'arraybuffer' },
  )

  return response.data
}

export const uploadFairShareBMU = async (file: FormData) => {
  const response: AxiosResponse<ArrayBuffer> = await api.post(
    endpoints.fairShare.uploadShareBMU,
    file,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      responseType: 'arraybuffer',
    },
  )

  return response
}

export const uploadFairShareOffice = async (file: FormData) => {
  const response: AxiosResponse<ArrayBuffer> = await api.post(
    endpoints.fairShare.uploadShareOffice,
    file,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      responseType: 'arraybuffer',
    },
  )

  return response
}

export const uploadFairShareMeta = async (file: FormData) => {
  const response: AxiosResponse<ArrayBuffer> = await api.post(
    endpoints.fairShare.uploadShareMeta,
    file,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      responseType: 'arraybuffer',
      validateStatus: (status) => status < 500,
    },
  )

  return response
}

export const getFairShareOffice = async (fairShareOfficePayload: FairShareOfficePayload) => {
  const { data } = await api.post(
    endpoints.fairShare.getOfficeFairShare,
    toFairShareOfficePayloadApi(fairShareOfficePayload),
  )

  return toFairShareOfficeResponse(data.data)
}

export const getFairShareOfficeByMaterial = async (
  fairShareOfficeByMaterialPayload: FairShareOfficeByMaterialPayload,
) => {
  const { data } = await api.post(
    endpoints.fairShare.getOfficeByMaterialFairShare,
    toFairShareOfficeByMaterialPayloadApi(fairShareOfficeByMaterialPayload),
  )

  return toFairShareByMaterialResponse(data.data)
}

export const FairShareOpenOfficeWallet = async (
  fairShareOpenWalletPayload: FairShareOpenWalletPayload,
) => {
  const response = await api.post(
    endpoints.fairShare.openOfficeWallets,
    toFairShareOpenWalletPayloadApi(fairShareOpenWalletPayload),
    { responseType: 'arraybuffer' },
  )

  return response
}

export const downloadFairShareOffice = async (
  fairShareOfficeDownloadPayload: FairShareOfficeDownload,
) => {
  const response: AxiosResponse<ArrayBuffer> = await api.post(
    endpoints.fairShare.downloadFairShareOffice,
    toFairShareOfficeDownloadPayloadApi(fairShareOfficeDownloadPayload),
    { responseType: 'arraybuffer' },
  )

  return response.data
}

export const getMaterialShares = async (materialSharePayload: MaterialSharePayload) => {
  const { data } = await api.post(
    endpoints.fairShare.getMaterialShare,
    toMaterialSharePayloadApi(materialSharePayload),
  )

  return toMaterialShareResponse(data.data)
}

export const postEditMaterialLevel = async (materialLevelPayload: MaterialLevelPayload) => {
  const response = await api.post(
    endpoints.fairShare.editMaterialLevel,
    toMaterialLevelPayloadApi(materialLevelPayload),
  )

  return response
}

export const getFairShareTracking = async (fairShareTrackingPayload: FairShareTrackingPayload) => {
  const { data } = await api.post(
    endpoints.fairShare.getFairShareTracking,
    toFairShareTrackingPayloadApi(fairShareTrackingPayload),
  )

  return toFairShareTrackingResponse(data.data)
}
